import { PERMISSIONS } from '@conversed/shared/src/central/constants/permissions';
import type { ChangePasswordDTO } from '@conversed/shared/src/central/dto/account.dto';
import type { UserProfileUpdateDTO } from '@conversed/shared/src/central/dto/user.dto';
import { StudioType } from '@conversed/shared/src/central/types/studio.type';
import { useFileMedia } from '@hooks/useFileMedia';
import { useCallback } from 'react';
import {
  meApi,
  updatePasswordApi,
  updateProfileApi,
  updateProfilePictureApi,
} from '../../api/userApi';
import { Actions } from '../../constants/actionTypes';
import { DataKeys } from '../../constants/login';
import { resizeProfilePic } from '../../helpers/imageHelper';
import {
  toastErrorTranslated,
  toastSuccessTranslated,
} from '../../helpers/toast';
import type { TCompany } from '../../interfaces/IAuth';
import { useMainDispatch } from '../useMainDispatch';

const fallBackToFirstCompany = (companies: TCompany[]) => {
  const [firstCompany] = companies;

  if (!firstCompany) {
    localStorage.removeItem(DataKeys.SELECTED_COMPANY_DATA_KEY);

    return undefined;
  }

  localStorage.setItem(
    DataKeys.SELECTED_COMPANY_DATA_KEY,
    firstCompany.identifier,
  );

  return firstCompany;
};

/**
 * Every request sends the stored company in the `user-company` header, and
 * the backend answers 403 for a company the user is no longer a member of.
 * A stored company that left the user's list is therefore replaced with the
 * first company the user still has. Nothing stored means no choice was made
 * yet, so the company selector shows.
 */
export const getSelectedCompany = (companies: TCompany[]) => {
  if (companies.length === 1) {
    const [company] = companies;
    localStorage.setItem(
      DataKeys.SELECTED_COMPANY_DATA_KEY,
      company.identifier,
    );
    return company;
  }

  const selectedCompanyId = localStorage.getItem(
    DataKeys.SELECTED_COMPANY_DATA_KEY,
  );

  if (!selectedCompanyId) {
    return undefined;
  }

  const storedCompany = companies.find(
    (company) => company.identifier === selectedCompanyId,
  );

  if (storedCompany) {
    return storedCompany;
  }

  return fallBackToFirstCompany(companies);
};

export const getSelectedCompanyId = (
  selectedCompanyIdentifier: string,
  companies: TCompany[],
) => {
  return companies?.find(
    (company) => company.identifier === selectedCompanyIdentifier,
  )?.id;
};

export const useCurrentUserActions = () => {
  const dispatch = useMainDispatch();
  const { getImageByURL } = useFileMedia();

  // * Returns the freshly mapped companies list so a caller that needs the
  // * refreshed data immediately (e.g. re-resolving a just-created company by id)
  // * does not have to wait for the Redux update to flow back through a re-render.
  const fetchCurrentUser = useCallback(async (): Promise<TCompany[]> => {
    const { data: userData } = await meApi();

    const selectedCompany = getSelectedCompany(userData.companies);

    // Get the permissions of the selected company, or default to an empty array
    const permissions = selectedCompany?.permissions || [];

    // If the selected company has no permissions or there is no selected company
    if (!permissions.length) {
      // Check if any of the user's companies have the "COMPANY_CREATE" permission
      const hasCreateCompanyPermission = userData.companies.some((company) =>
        company.permissions.includes(PERMISSIONS.COMPANY.COMPANY_CREATE),
      );

      // If a company with the "COMPANY_CREATE" permission is found, assign this permission to the permissions array. If not, leave the permissions array empty.
      // This allows the user to access the "create company" feature (represented by a plus button) automatically, without needing to select a company first.
      if (hasCreateCompanyPermission) {
        permissions.push(PERMISSIONS.COMPANY.COMPANY_CREATE);
      }
    }

    const mappedCompanies = userData.companies.map((company) => ({
      id: company.id,
      identifier: company.identifier,
      name: company.name,
      permissions: company.permissions,
      inboxes: company.inboxes,
      icon: getImageByURL(company.icon, company.identifier),
      websiteUrl: company?.websiteUrl,
      isActive: company.isActive,
      createdAt: company.createdAt,
      thirdPartyIntegrations: company?.thirdPartyIntegrations || [],
      platformFeatures: company?.platformFeatures || [],
      roles: company?.roles || [],
      packages: company?.packages,
      studioType: company?.studioType || StudioType.CHAT_AI,
      parentCompanyId: company?.parentCompanyId?.toString(),
      provisionStatus: company?.provisionStatus,
    }));

    dispatch({
      type: Actions.SET_USER,
      payload: {
        currentUser: {
          id: userData.id,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          profilePic: userData.profilePic,
          selectedCompanyId: selectedCompany?.identifier,
          mfaEnabled: userData.mfaEnabled,
          companies: mappedCompanies,
          isSuperAdmin: userData.isSuperAdmin ?? false,
        },
        permissions,
      },
    });

    return mappedCompanies;
  }, [dispatch, getImageByURL]);

  const updateProfile = useCallback(
    async (profile: UserProfileUpdateDTO) => {
      try {
        const { data: profileUpdated } = await updateProfileApi(profile);
        dispatch({
          type: Actions.UPDATE_CURRENT_USER,
          payload: {
            firstName: profileUpdated.firstName,
            lastName: profileUpdated.lastName,
            email: profileUpdated.email,
          },
        });
        toastSuccessTranslated(
          'account-settings.profile.update-profile.toast.success',
        );
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      }
    },
    [dispatch],
  );

  const updateProfilePicture = useCallback(
    async (profilePicFile: File) => {
      dispatch({
        type: Actions.SET_UI,
        payload: {
          isLoading: true,
        },
      });
      try {
        const profilePicBase64 = await resizeProfilePic(profilePicFile);
        const { data: profileUpdated } = await updateProfilePictureApi({
          profilePicBase64,
        });
        dispatch({
          type: Actions.UPDATE_CURRENT_USER,
          payload: {
            profilePic: profileUpdated?.profilePic,
          },
        });
        toastSuccessTranslated(
          'account-settings.profile.update-profile-picture.toast.success',
        );
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({
          type: Actions.SET_UI,
          payload: {
            isLoading: false,
          },
        });
      }
    },
    [dispatch],
  );

  const updatePassword = useCallback(
    async (passwordPayload: ChangePasswordDTO) => {
      dispatch({
        type: Actions.SET_UI,
        payload: {
          isLoading: true,
        },
      });
      try {
        await updatePasswordApi(passwordPayload);
        toastSuccessTranslated(
          'account-settings.profile.update-password.toast.success',
        );
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({
          type: Actions.SET_UI,
          payload: {
            isLoading: false,
          },
        });
      }
    },
    [dispatch],
  );

  return {
    updatePassword,
    fetchCurrentUser,
    updateProfile,
    updateProfilePicture,
  };
};
